import React, { useEffect, useMemo, useState } from "react";
import { Card, Form, Button, InputGroup, ListGroup, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import { baseURL } from "../helpers/baseURL";
import { headers } from '../helpers/Requests';

const IncomingEmailTicketRecipient = () => {
  const [email, setEmail] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [error, setError] = useState("");

  const normalizedEmail = useMemo(() => email.trim().toLowerCase(), [email]);

  const load = async () => {
    setError("");
    setIsLoading(true);

    try {
      const res = await axios.get(`${baseURL}incoming-email-recipients`, headers);
      setItems(res?.data?.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load recipients.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setError("");

    if (!normalizedEmail) {
      setError("Please enter an email address.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      setError("Please enter a valid email.");
      return;
    }

    if (items.some((x) => (x.email || "").toLowerCase() === normalizedEmail)) {
      setError("This email already exists.");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${baseURL}incoming-email-recipients`, { email: normalizedEmail }, headers);
      setEmail("");
      await load(); // backend doesn't return created row, so reload list
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to add recipient.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setError("");
    setRemovingId(id);

    try {
      await axios.delete(`${baseURL}incoming-email-recipients/${id}`, headers);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to remove recipient.");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Card>
      <Card.Body>
        <Card.Title className="mb-3">Incoming Email Recipients</Card.Title>

        <Form onSubmit={add}>
          <InputGroup>
            <Form.Control
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Type Email here"
              disabled={saving}
            />
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? "Adding..." : "Add"}
            </Button>
          </InputGroup>
        </Form>

        {error ? (
          <Alert variant="danger" className="py-2 mt-3 mb-0">
            {error}
          </Alert>
        ) : null}

        <div className="mt-3">
          {loading ? (
            <div className="text-muted d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" /> Loading...
            </div>
          ) : items.length === 0 ? (
            <div className="text-muted">No recipients yet.</div>
          ) : (
            <ListGroup>
              {items.map((item) => (
                <ListGroup.Item
                  key={item.id}
                  className="d-flex justify-content-between align-items-center"
                >
                  <span className="text-truncate">{item.email}</span>

                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="ms-2"
                    onClick={() => remove(item.id)}
                    disabled={removingId === item.id}
                    title="Remove"
                    type="button"
                  >
                    {removingId === item.id ? "..." : "x"}
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default IncomingEmailTicketRecipient;
